import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { PowerSchedule } from '@api/index';
import { IdName } from '@api/models/idname';
import { DeviceServiceStatusEnum } from '@app/shared/models/enums/deviceServiceStatusEnum';
import { TrackingEnum } from '@app/shared/models/enums/deviceTracking.enum';
import { DeviceType } from '@app/shared/models/enums/deviceType.enum';
import { DateTime } from 'luxon';

export interface ScreenServicesStateData {
  advertiserServiceIsAlive: boolean;
  aiServiceIsAlive?: boolean;
  redisIsAlive: boolean;
  aiFps?: AiFPS;
  screenServiceStatus: DeviceServiceStatusEnum;
  screenAsleepStatus: ScreenAsleepStatusEnum;
  lastAliveDate: string;
  appVersions: AppVersions;
  currentDeviceAppVersions: AppVersions;
}

export interface AppVersions {
  advertiserVersion: string;
  aiVersion: string;
  managerVersion: string;
}

export interface AiFPS {
  fpS_threshold: string | null;
  fps: string | null;
  isFpsBelowThreshold : boolean | null;
}

export enum ScreenAsleepStatusEnum {
  Awake = 1,
  Asleep = 2,
}

export interface ScreenDefaultMedia {
  media: IdName;
  fileName: string;
  orderId: number;
  presentationTime: number;
  mediaFileThumbnailStreamingLink: string;
  mediaFileStreamingLink: string;
  type: number;
}

export interface ServiceStatus {
  name: string;
  isAlive?: boolean;
  lastAlive?: string;
  translationKey: string;
  statusKey: string;
}

export interface ServiceVersionMismatch {
  name: string;
  deviceVersion: string;
  flowVersion: string;
}

export interface SaveScreenRequest {
  id: number;
  aiVersion?: string;
  advertiserVersion?: string;
  channelId?: number;
  channelDescription?: string;
  humanUniqueIdentifier?: string;
  orientation: number;
  note?: string;
  deviceType: DeviceType;
  trackingType: TrackingEnum;
  placementId?: number;
  customerId: number;
  latitude: number;
  longitude: number;
  powerSchedule?: PowerSchedule[];
  screenDefaultMediaFiles?: ScreenDefaultMediaCreateOrUpdate[];
  isUsedForTrackingVehicle?: boolean;
  sendSmsNotifications?: boolean;
  screenSmsNotificationConfiguration?: ScreenSmsNotificationsRequest;
  contactPersonIds?: number[];
  settings?: string;
  configSettings?: string | null;
}

export interface SaveSensorSettingsRequest {
  sensorModelId: number;
  zoom?: number | null;
  connectionString?: string | null;
}

export interface ShowVideoFeedResponse {
  minutes: number;
}

export interface DeviceSensorModelOption {
  id: number;
  name: string;
  adjustableZoom: boolean;
  ipBased: boolean;
  isDefault?: boolean;
}

export interface ScreenDefaultMediaCreateOrUpdate {
  orderId: number;
  presentationTime: number;
  mediaId: number;
  fileName: string;
}

export enum DeviceSteps {
  GeneralInfo = 'general-info',
  Details = 'details',
}

export enum DeviceStepIndex {
  GeneralInfo = 0,
  Details = 1,
}

export interface GeneralInfoFormGroup {
  id: FormControl<number>;
  humanUniqueIdentifier: FormControl<string>;
  channelId: FormControl<number>;
  channelDescription: FormControl<any>;
  deviceType: FormControl<number>;
  note: FormControl<string>;
  customerId: FormControl<number>;
  isUsedForTrackingVehicle: FormControl<boolean>;
  shouldSendSmsOnStatusChange: FormControl<boolean>;
  smsNotificationsConfigurations?: FormGroup<any>;
  contactPersonIds?: FormControl<any>;
  financialLifeStartDate: FormControl<DateTime | null>;
  financialLifeEndDate: FormControl<DateTime | null>;
}

export interface DetailsFormGroup {
  latitude: FormControl<number>;
  longitude: FormControl<number>;
  orientation: FormControl<number>;
  aiVersion: FormControl<string>;
  advertiserVersion: FormControl<string>;
  tracking: FormControl<number>;
  scheduleOptions: FormControl<boolean>;
  defaultMediaFiles: FormArray<any>;
  monday?: FormGroup<any>;
  tuesday?: FormGroup<any>;
  wednesday?: FormGroup<any>;
  thursday?: FormGroup<any>;
  friday?: FormGroup<any>;
  saturday?: FormGroup<any>;
  sunday?: FormGroup<any>;
  managerVersion: FormControl<string>;
  serialNumber: FormControl<string>;
}

export interface SettingsFormGroup {
  aiLocalSettings: FormControl<string | null>;
  configSettings: FormControl<string | null>;
}

export interface SensorDetailsFormGroup {
  sensorModelId: FormControl<number | null>;
  sensorZoomPercentage: FormControl<number | null>;
  sensorConnectionString: FormControl<string | null>;
}

export interface ScreenSmsNotifications {
  smsAlertOnDeviceDegradation: boolean;
  smsAlertOnDeviceRestored: boolean;
  startTimeSmsNotifications: string;
  endTimeSmsNotifications: string;
  smsContactPersons: IdName[];
  timeZone: string;
}

export interface ScreenSmsNotificationsRequest {
  smsStartHour: number;
  smsStartMinutes: number;
  smsEndHour: number;
  smsEndMinutes: number;
  smsAlertOnDeviceDegradation: boolean;
  smsAlertOnDeviceRestored: boolean;
  utcOffset: string;
}

export interface ScreenSmsNotifications {
  smsAlertOnDeviceDegradation: boolean;
  smsAlertOnDeviceRestored: boolean;
  startTimeSmsNotifications: string;
  endTimeSmsNotifications: string;
  smsContactPersons: IdName[];
  timeZone: string;
}

export interface ScreenSmsNotificationsRequest {
  smsStartHour: number;
  smsStartMinutes: number;
  smsEndHour: number;
  smsEndMinutes: number;
  smsAlertOnDeviceDegradation: boolean;
  smsAlertOnDeviceRestored: boolean;
  utcOffset: string
}

export enum DeviceStateEnum {
  Offline = 1,
  Sleep = 2,
  Warning = 3,
  Online = 4,
  New = 5,
  Unknown = 6,
}

enum DeviceStatusColors {
  Offline = '#fc4b47',
  Sleep = '#1f2937',
  Warning = '#ecc94b',
  Online = '#177578',
  New = '#1f5792',
  Unknown = '#5b21b6',
}

export const DEVICE_STATUS_COLORS = [
  { id: DeviceStateEnum.Offline, name: 'global.offline', hexCode: DeviceStatusColors.Offline },
  { id: DeviceStateEnum.Sleep, name: 'global.sleep', hexCode: DeviceStatusColors.Sleep },
  { id: DeviceStateEnum.Warning, name: 'global.warning', hexCode: DeviceStatusColors.Warning },
  { id: DeviceStateEnum.Online, name: 'global.online', hexCode: DeviceStatusColors.Online },
  { id: DeviceStateEnum.New, name: 'global.new', hexCode: DeviceStatusColors.New },
  { id: DeviceStateEnum.Unknown, name: 'global.unknown', hexCode: DeviceStatusColors.Unknown },
];

export const DEVICE_STATUS_COLOR_HEX_BY_ID = Object.fromEntries(DEVICE_STATUS_COLORS.map(color => [color.id, color.hexCode]));

export enum DeviceConfigSettingsStateEnum {
  Created = 1,
  Reset,
  Initialized,
  Registered
}

export interface ResetRegistrationResult {
  anyDeskConnectionId: string | null;
  configSettingsState: IdName;
  date: string | null;
  serialNumber: string | null;
}